/**
 * Storefront loader for Pokémon singles with fallback when smart collection is empty.
 */
(() => {
  const SINGLES_FALLBACK_HANDLE = 'all';
  const SINGLES_TAGS = new Set(['einzelkarte', 'pokemon-single']);
  const PARALLEL_PAGES = 3;

  const isSingleListingProduct = (product) => {
    if (!product || typeof product !== 'object') return false;
    const tags = Array.isArray(product.tags) ? product.tags : [];
    if (tags.some((tag) => SINGLES_TAGS.has(String(tag).trim().toLowerCase()))) return true;
    const type = String(product.product_type || '').toLowerCase();
    return type.includes('einzelkarte');
  };

  const fetchProductsJsonPage = async (origin, handle, pageNum, timeoutMs) => {
    const url = `${origin}/collections/${encodeURIComponent(handle)}/products.json?limit=250&page=${pageNum}`;
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await fetch(url, { signal: controller.signal });
      if (!res.ok) return null;
      const data = await res.json();
      return data.products || [];
    } catch (error) {
      console.warn('[Singles-Katalog] products.json', handle, 'page', pageNum, error);
      return null;
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  const emitBatch = (onBatch, chunk, meta) => {
    if (!chunk.length || typeof onBatch !== 'function') return;
    onBatch(chunk, meta);
  };

  const loadFromHandle = async (origin, handle, { maxPages, timeoutMs, filterSingles, onBatch }) => {
    const items = [];

    const consumeBatch = (batch, pageNum) => {
      if (!batch?.length) return false;
      const chunk = filterSingles ? batch.filter(isSingleListingProduct) : batch;
      items.push(...chunk);
      emitBatch(onBatch, chunk, { page: pageNum, partial: batch.length >= 250 });
      return batch.length >= 250;
    };

    const first = await fetchProductsJsonPage(origin, handle, 1, timeoutMs);
    if (first == null) return items;
    if (!consumeBatch(first, 1)) return items;

    let page = 2;
    while (page <= maxPages) {
      const pageNums = [];
      for (let i = 0; i < PARALLEL_PAGES && page + i <= maxPages; i += 1) {
        pageNums.push(page + i);
      }

      const results = await Promise.all(
        pageNums.map((pageNum) => fetchProductsJsonPage(origin, handle, pageNum, timeoutMs))
      );

      let hasMore = false;
      for (let i = 0; i < results.length; i += 1) {
        const batch = results[i];
        if (batch == null) return items;
        if (!batch.length) return items;
        if (consumeBatch(batch, pageNums[i])) hasMore = true;
        else return items;
      }

      if (!hasMore) break;
      page += PARALLEL_PAGES;
    }

    return items;
  };

  const fetchSinglesProducts = async (options = {}) => {
    const {
      collectionHandle = 'pokemon-singles',
      maxPages = 20,
      timeoutMs = 15000,
      onBatch = null,
    } = options;
    const origin = window.location.origin;

    let products = await loadFromHandle(origin, collectionHandle, {
      maxPages,
      timeoutMs,
      filterSingles: false,
      onBatch,
    });
    let usedFallback = false;

    if (!products.length && collectionHandle !== SINGLES_FALLBACK_HANDLE) {
      usedFallback = true;
      console.warn(
        `[Singles-Katalog] Kollektion „${collectionHandle}“ leer – Fallback „${SINGLES_FALLBACK_HANDLE}“ + Einzelkarte-Filter`
      );
      products = await loadFromHandle(origin, SINGLES_FALLBACK_HANDLE, {
        maxPages,
        timeoutMs,
        filterSingles: true,
        onBatch,
      });
    }

    return { products, usedFallback };
  };

  window.Kl24SinglesCatalog = {
    isSingleListingProduct,
    fetchSinglesProducts,
  };
})();
